/** The Pre-check-in tab's sections (plan §3), in the guest's reading order, one column:
 *  Flow · Welcome · Lead booker · Party members · Waivers · Confirmation · Custom fields.
 *
 *  - Every value is a FieldSlot of the operator's record (the tour editor's labels and help,
 *    the wording rule), every list the shared pre-check pieces on their edit geometry in
 *    both modes — pressing E swaps controls in place (Zero-Re-Layout): the heading-row
 *    action ("Insert variable") and the add rows keep their room in view mode.
 *  - Working in a section turns the preview to what a guest sees of it (focus or a press
 *    inside it): Flow, Welcome, Lead booker → the lead booker's step; Party members →
 *    Guest 1; Waivers → the Pre-check-in step; Confirmation → Done.
 *  - The texts carry a counter (plain text, one language); the confirmation message takes
 *    template variables through "Insert variable" (`options.variables`), at the caret. */
import DataObjectIcon from '@mui/icons-material/DataObject';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Button, ListSubheader, Menu, MenuItem, Typography } from '@mui/material';
import { useId, useRef, useState, type FocusEvent, type ReactNode } from 'react';

import { PRECHECK } from '@/features/precheck/model';
import { useDraftField } from '@/features/records/draft/DraftProvider';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { FieldCellGrid } from '@/features/records/fields/FieldCellGrid';
import { FieldSlot } from '@/features/records/fields/FieldSlot';
import { FIELD_GRID_SX } from '@/features/records/panels/fieldGrid';
import { CHECKIN_STEP, DONE_STEP, guestStep, LEAD_STEP, type GuestPreviewStep } from '@/features/precheck/preview/previewModel';

import { useOperatorPrecheck, usePreviewStep } from './context';
import { CustomFieldsTable } from './CustomFieldsTable';
import { COLLECT_PARTY_PATH, PARTY_NOT_COLLECTED } from './editorConfig';
import { OPERATOR_ENTITY, TEXT_MAX_LENGTH } from './model';
import { sampleVariables, templateVariables } from './PrecheckPreview';
import { FieldsView, QuestionsView, WaiversView } from './widgets';

const P = PRECHECK;

/** Widgets that want the whole row. */
const WIDE = new Set(['textarea', 'precheck_fields', 'precheck_categories', 'precheck_waivers']);

export const sectionDomId = (id: string) => `precheck-${id}`;

function Section({
  id,
  title,
  step,
  action,
  children,
}: {
  id: string;
  title: string;
  /** The preview step this section shows; none: the preview stays where it is. */
  step?: GuestPreviewStep;
  /** The heading row's action — its room is kept in view mode. */
  action?: ReactNode;
  children: ReactNode;
}) {
  const titleId = useId();
  const { setStep } = usePreviewStep();
  const follow = step ? () => setStep(step) : undefined;
  return (
    <Box
      component="section"
      id={sectionDomId(id)}
      aria-labelledby={titleId}
      data-precheck-section={id}
      onFocusCapture={follow}
      onPointerDownCapture={follow}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        p: 2,
        display: 'grid',
        gap: 1,
        minWidth: 0,
        scrollMarginTop: 16,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 30 }}>
        <Typography id={titleId} component="h3" variant="subtitle2" color="text.secondary" sx={{ m: 0, flex: 1, minWidth: 0 }}>
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </Box>
  );
}

/** FieldSlots of the operator's record in the pinned field grid (232 px columns). */
function OperatorFieldGrid({
  keys,
  displays,
  labels,
}: {
  keys: string[];
  /** A key's view-mode rendering of its stored value (a list as a list). */
  displays?: Partial<Record<string, ReactNode>>;
  /** A key's label on the page when the catalog's (the ledger's) names more context. */
  labels?: Partial<Record<string, string>>;
}) {
  const { fieldByKey } = useRecordEditor();
  const { topId, baseline } = useOperatorPrecheck();
  return (
    <FieldCellGrid
      sx={FIELD_GRID_SX}
      cells={keys.map((key) => {
        const field = fieldByKey.get(key);
        return {
          key: `${baseline.rec}:${key}`,
          rec: baseline.rec,
          path: key,
          wide: Boolean(field?.widget && WIDE.has(field.widget)),
          node: (
            <FieldSlot
              fieldKey={key}
              entity={OPERATOR_ENTITY}
              id={topId}
              path={key}
              inlineChoices={false}
              {...(labels?.[key] ? { label: labels[key] } : {})}
              {...(displays && key in displays ? { display: displays[key] } : {})}
            />
          ),
        };
      })}
    />
  );
}

/** "312 / 10,000 characters" under a text — the stored text's in view mode, the draft's in
 *  edit mode, one line in both. */
function TextCounter({ path, note }: { path: string; note: string }) {
  const { topId } = useOperatorPrecheck();
  const { mode } = useRecordEditor();
  const f = useDraftField(OPERATOR_ENTITY, topId, path);
  const text = mode === 'edit' ? f.value : f.baseline;
  const n = typeof text === 'string' ? text.length : 0;
  return (
    <Typography variant="caption" color={n > TEXT_MAX_LENGTH ? 'error' : 'text.secondary'} sx={{ fontVariantNumeric: 'tabular-nums', mt: -0.5 }} data-text-counter={path}>
      {n.toLocaleString('en')} / {TEXT_MAX_LENGTH.toLocaleString('en')} characters · {note}
    </Typography>
  );
}

/** "Insert variable": a template variable at the confirmation message's caret (at its end
 *  when it has not been focused). Edit mode only; its room is kept in view mode. */
function InsertVariable({ caret }: { caret: { current: { start: number; end: number } | null } }) {
  const { topId, baseline } = useOperatorPrecheck();
  const { mode } = useRecordEditor();
  const path = P('confirmation_message');
  const f = useDraftField(OPERATOR_ENTITY, topId, path);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const variables = templateVariables(baseline.data.options.variables);
  const samples = sampleVariables(baseline.data.options.variables);
  const editing = mode === 'edit';
  const insert = (variable: string) => {
    setAnchor(null);
    const cur = typeof f.value === 'string' ? f.value : '';
    const at = caret.current ?? { start: cur.length, end: cur.length };
    const token = `{{ ${variable} }}`;
    f.set(cur.slice(0, at.start) + token + cur.slice(at.end), { label: `Insert ${token}` });
    const next = at.start + token.length;
    caret.current = { start: next, end: next };
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLTextAreaElement>(`#${sectionDomId('confirmation')} textarea:not([aria-hidden="true"])`);
      el?.focus();
      el?.setSelectionRange(next, next);
    });
  };
  const groups = new Map<string, string[]>();
  for (const v of variables) {
    const head = v.split('.').slice(0, -1).join(' · ');
    groups.set(head, [...(groups.get(head) ?? []), v]);
  }
  return (
    <>
      <Button
        size="small"
        startIcon={<DataObjectIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        disabled={!editing || variables.length === 0}
        aria-haspopup="menu"
        aria-controls={anchor ? menuId : undefined}
        aria-expanded={Boolean(anchor)}
        // View mode keeps its room: the heading row never moves on E.
        sx={editing ? undefined : { visibility: 'hidden' }}
        {...(editing ? {} : { 'aria-hidden': true, tabIndex: -1 })}
        data-insert-variable
      >
        Insert variable
      </Button>
      <Menu id={menuId} anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} MenuListProps={{ dense: true }}>
        {[...groups.entries()].flatMap(([head, vs]) => [
          <ListSubheader key={`h:${head}`} sx={{ lineHeight: '28px', textTransform: 'capitalize' }}>
            {head}
          </ListSubheader>,
          ...vs.map((v) => (
            <MenuItem key={v} onClick={() => insert(v)} sx={{ gap: 1.5 }}>
              <Box component="code" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: '0.8125rem' }}>
                {`{{ ${v} }}`}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {samples[v]}
              </Typography>
            </MenuItem>
          )),
        ])}
      </Menu>
    </>
  );
}

function PartyNotCollectedNote() {
  const { topId } = useOperatorPrecheck();
  const { mode } = useRecordEditor();
  const f = useDraftField(OPERATOR_ENTITY, topId, COLLECT_PARTY_PATH);
  const on = (mode === 'edit' ? f.value : f.baseline) !== false;
  if (on) return null;
  return (
    <Typography variant="caption" color="warning.dark" sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }} data-party-not-collected>
      <WarningAmberIcon aria-hidden sx={{ fontSize: 14, mt: '2px' }} />
      {PARTY_NOT_COLLECTED}.
    </Typography>
  );
}

export function PrecheckSections() {
  // Where the confirmation message's caret was when it lost focus (Insert variable).
  const caret = useRef<{ start: number; end: number } | null>(null);
  const onConfirmationBlur = (e: FocusEvent<HTMLElement>) => {
    const t = e.target;
    if (t instanceof HTMLTextAreaElement) caret.current = { start: t.selectionStart, end: t.selectionEnd };
  };
  return (
    <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }} data-precheck-sections>
      <Section id="flow" title="Flow" step={LEAD_STEP}>
        <OperatorFieldGrid keys={[P('enabled'), P('required'), P('allow_edit_after_checkin')]} />
      </Section>
      <Section id="welcome" title="Welcome" step={LEAD_STEP}>
        <OperatorFieldGrid keys={[P('intro_text')]} />
        <TextCounter path={P('intro_text')} note="plain text, one language" />
      </Section>
      <Section id="lead" title="Lead booker" step={LEAD_STEP}>
        <OperatorFieldGrid
          keys={[P('lead_booker_fields'), P('lead_selected_tag_categories')]}
          displays={{ [P('lead_booker_fields')]: <FieldsView scope="lead" />, [P('lead_selected_tag_categories')]: <QuestionsView scope="lead" /> }}
          labels={{ [P('lead_selected_tag_categories')]: 'Tag-category questions' }}
        />
      </Section>
      <Section id="party" title="Party members" step={guestStep(1)}>
        <OperatorFieldGrid keys={[P('collect_party_members_info'), P('include_party_members_in_chat'), P('apply_same_fields_to_party_members')]} />
        <PartyNotCollectedNote />
        <OperatorFieldGrid
          keys={[P('party_member_fields'), P('party_selected_tag_categories')]}
          displays={{ [P('party_member_fields')]: <FieldsView scope="party" />, [P('party_selected_tag_categories')]: <QuestionsView scope="party" /> }}
          labels={{ [P('party_selected_tag_categories')]: 'Tag-category questions' }}
        />
      </Section>
      <Section id="waivers" title="Waivers" step={CHECKIN_STEP}>
        <OperatorFieldGrid keys={[P('waiver_enabled'), P('waivers')]} displays={{ [P('waivers')]: <WaiversView /> }} />
      </Section>
      <Section id="confirmation" title="Confirmation" step={DONE_STEP} action={<InsertVariable caret={caret} />}>
        <Box onBlurCapture={onConfirmationBlur} sx={{ minWidth: 0 }}>
          <OperatorFieldGrid keys={[P('confirmation_message')]} />
        </Box>
        <TextCounter path={P('confirmation_message')} note="variables filled in for each guest" />
      </Section>
      <Section id="custom-fields" title="Custom fields">
        <CustomFieldsTable />
      </Section>
    </Box>
  );
}
